import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  Key,
  ArrowsLeftRight,
  ArrowClockwise,
  Buildings,
  ShieldCheck,
  Briefcase,
  HeartStraight,
  ArrowRight,
  ArrowUpRight,
  SealCheck,
  Handshake,
  CurrencyGbp,
  UserFocus,
  Phone,
  MagnifyingGlass,
  RocketLaunch,
  Gift,
  Quotes,
} from "@phosphor-icons/react";
import Seo from "../components/Seo";
import { Reveal, MaskedLine } from "../components/Reveal";
import TrustindexWidget from "../components/TrustindexWidget";
import { MiniEstimate, RepaymentCalculator } from "../components/calculators";
import {
  IMAGES,
  GALLERY,
  SERVICES,
  WHY_BTK,
  LENDERS,
  HOW_IT_WORKS,
  ABOUT,
  BEN_PHOTO_URL,
  FEATURED_QUOTES,
  CALC_RISK_WARNING,
} from "../data/content";

const SERVICE_ICONS = {
  "first-time-buyer": Key,
  "next-time-buyer": ArrowsLeftRight,
  remortgage: ArrowClockwise,
  "buy-to-let": Buildings,
  protection: ShieldCheck,
  "whole-of-life": HeartStraight,
  "business-protection": Briefcase,
};

const WHY_ICONS = [SealCheck, CurrencyGbp, Handshake, UserFocus];
const HOW_ICONS = [Phone, MagnifyingGlass, RocketLaunch];

const Eyebrow = ({ children, tone = "brand", className = "" }) => (
  <p className={`eyebrow ${tone === "light" ? "text-white/70" : tone === "gold" ? "text-brand-gold" : "text-brand"} ${className}`}>
    <span className="rule-line" />
    {children}
  </p>
);

const Hero = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.14]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="hero-grain relative overflow-hidden bg-brand-night pt-[76px] text-white"
    >
      <motion.img
        src={IMAGES.hero}
        alt="A premium UK period property at golden hour"
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 h-[115%] w-full object-cover will-change-transform"
      />
      <div className="hero-vignette absolute inset-0" />
      <img
        aria-hidden
        src="/b-mark.png"
        alt=""
        className="pointer-events-none absolute right-[-4rem] bottom-[-4rem] w-[320px] select-none opacity-[0.10] rotate-6 sm:w-[460px] lg:right-[-5rem] lg:bottom-[-6rem] lg:w-[640px]"
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid min-h-[86vh] items-end gap-14 py-20 lg:min-h-[88vh] lg:grid-cols-12 lg:items-center lg:py-28">
          <div className="lg:col-span-8 xl:col-span-7">
            <MaskedLine delay={0.1}>
              <span className="inline-flex items-center gap-3 text-[0.72rem] font-semibold uppercase tracking-[0.32em] text-brand-gold">
                <span className="h-px w-10 bg-brand-gold" />
                Established · Appointed Representative of PRIMIS · Whole of market
              </span>
            </MaskedLine>

            <h1 className="editorial-h mt-8 text-[2.6rem] font-medium leading-[1.02] text-white sm:text-[3.6rem] lg:text-[4.4rem]">
              <MaskedLine delay={0.2}>A boutique mortgage</MaskedLine>
              <MaskedLine delay={0.33}>
                &amp; protection practice
              </MaskedLine>
              <MaskedLine delay={0.46}>
                <span className="italic text-white/70">for homeowners who care</span> about the details.
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-8 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg"
            >
              Berkshire based, serving Berkshire, London, Surrey and the rest of the UK.
              Whole-of-market advice across 90+ lenders — one named advisor from first call
              to keys.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.74 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-book-call-btn"
                onClick={() => navigate("/contact")}
                className="group inline-flex items-center gap-2 rounded-none bg-brand px-8 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.22em] text-white shadow-[0_14px_40px_rgba(1,86,207,0.45)] transition-[background-color,transform] duration-200 hover:bg-brand-dark hover:-translate-y-0.5"
              >
                Arrange a consultation
                <ArrowRight size={14} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              <Link
                to="/calculators"
                data-testid="hero-calc-link"
                className="inline-flex items-center gap-2 rounded-none border border-white/40 bg-transparent px-8 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.22em] text-white transition-[background-color,border-color] duration-200 hover:border-white hover:bg-white/5"
              >
                Check your borrowing
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="hidden lg:col-span-4 xl:col-span-5 lg:block"
          >
            <div className="ml-auto w-full max-w-sm">
              <MiniEstimate />
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-6 left-5 right-5 sm:left-8 sm:right-8 flex items-end justify-between text-[0.68rem] font-semibold uppercase tracking-[0.32em] text-white/55">
          <span>Est. 2024 · Berkshire</span>
          <span className="hidden sm:inline">Scroll ↓</span>
        </div>
      </div>
    </section>
  );
};

const LenderMarquee = () => (
  <div data-testid="marquee-strip" className="overflow-hidden border-y border-white/5 bg-brand-night py-6">
    <div className="marquee-track flex w-max items-center gap-14 whitespace-nowrap">
      {[...LENDERS, ...LENDERS].map((item, i) => (
        <span key={i} className="flex items-center gap-14 font-serif text-sm uppercase tracking-[0.35em] text-white/55">
          {item}
          <span aria-hidden className="text-brand-gold text-xs">◆</span>
        </span>
      ))}
    </div>
  </div>
);

const RatesStrip = () => (
  <section data-testid="rates-strip" className="bg-brand-bone border-b border-black/5">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10 sm:py-14">
      <div className="flex flex-wrap items-center justify-between gap-6 mb-8">
        <Reveal>
          <p className="eyebrow text-brand"><span className="rule-line" />Current market snapshot</p>
          <h3 className="editorial-h mt-3 text-xl text-brand-ink sm:text-2xl">Illustrative mortgage rates</h3>
        </Reveal>
        <Reveal delay={0.08}>
          <Link
            to="/calculators"
            data-testid="rates-strip-cta"
            className="group inline-flex items-center gap-2 text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-brand transition-colors duration-200 hover:text-brand-dark"
          >
            Run your numbers
            <ArrowRight size={13} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {MARKET_RATES.map((r, i) => (
          <Reveal key={r.term} delay={i * 0.08}>
            <div
              data-testid={`rates-card-${i}`}
              className="flex items-end justify-between border border-black/10 bg-white px-6 py-7 transition-[border-color] duration-200 hover:border-brand/40"
            >
              <div>
                <p className="text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-brand">{r.term}</p>
                <p className="editorial-h mt-3 text-4xl text-brand-ink">{r.rate}</p>
              </div>
              <div className="text-right text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-neutral-500">
                <p>{r.fee}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-5 text-[11px] text-neutral-500">{RATES_DATE}</p>
    </div>
  </section>
);

const Intro = () => (
  <section className="bg-brand-bone py-28 sm:py-36">
    <div className="mx-auto max-w-5xl px-5 sm:px-8 text-center">
      <Reveal>
        <Eyebrow className="justify-center inline-flex">Established 2024</Eyebrow>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="editorial-h mt-8 text-3xl text-brand-ink sm:text-5xl lg:text-[3.4rem]">
          A boutique practice offering <em className="text-brand">considered advice</em> to private
          clients seeking mortgages for UK property.
        </h2>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mx-auto mt-10 max-w-2xl text-base leading-relaxed text-neutral-700 sm:text-lg">
          Our mission is to make the purchase or protection of property feel calm and
          certain. Honest advice, drawn from carefully nurtured relationships with banks,
          building societies and insurers — delivered in plain English.
        </p>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="mt-10 flex items-center justify-center gap-6">
          <span className="gold-divider" />
          <Link
            to="/about"
            data-testid="intro-meet-link"
            className="group inline-flex items-center gap-2 text-[0.72rem] font-semibold uppercase tracking-[0.32em] text-brand-ink transition-colors duration-200 hover:text-brand"
          >
            Meet the practice
            <ArrowRight size={14} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
          <span className="gold-divider" />
        </div>
      </Reveal>
    </div>
  </section>
);

const WIDE_INDEXES = new Set([0, 2]);

const Services = () => (
  <section className="bg-white py-28 sm:py-36">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <Reveal>
          <Eyebrow>What we do</Eyebrow>
          <h2 className="editorial-h mt-6 max-w-2xl text-3xl text-brand-ink sm:text-5xl lg:text-[3.4rem]">
            Every kind of move,<br />
            <span className="italic text-brand">one advisor</span> beside you.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="max-w-sm text-base leading-relaxed text-neutral-600">
            From a first flat in London to a country move in Berkshire, we tailor the
            mortgage, the protection and the plan.
          </p>
        </Reveal>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => {
          const Icon = SERVICE_ICONS[s.slug];
          const wide = WIDE_INDEXES.has(i);
          return (
            <Reveal key={s.slug} delay={(i % 3) * 0.08} className={wide ? "lg:col-span-2" : ""}>
              <Link
                to={`/mortgages/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className={`group flex h-full flex-col overflow-hidden border border-black/10 bg-brand-bone transition-[box-shadow,transform,border-color] duration-300 hover:-translate-y-1 hover:border-brand/30 hover:shadow-[0_30px_80px_rgba(10,19,38,0.12)] ${
                  wide ? "sm:flex-row" : ""
                }`}
              >
                {wide && (
                  <div className="h-56 shrink-0 overflow-hidden sm:h-auto sm:w-1/2">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="flex flex-1 flex-col justify-between gap-8 p-10">
                  <div className="flex items-start justify-between">
                    <span className="inline-flex h-11 w-11 items-center justify-center border border-brand/20 text-brand">
                      <Icon size={20} weight="duotone" />
                    </span>
                    <ArrowUpRight
                      size={20}
                      weight="bold"
                      className="text-neutral-400 transition-[color,transform] duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-brand"
                    />
                  </div>
                  <div>
                    <p className="eyebrow text-brand/80">0{i + 1}</p>
                    <h3 className="editorial-h mt-3 text-2xl text-brand-ink">{s.title}</h3>
                    <p className="mt-4 text-sm leading-relaxed text-neutral-600">{s.tagline}</p>
                  </div>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const Mosaic = () => {
  const [index, setIndex] = useState(0);
  const slides = GALLERY;

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), 4500);
    return () => clearInterval(id);
  }, [slides.length]);

  const smallA = slides[(index + 1) % slides.length];
  const smallB = slides[(index + 2) % slides.length];
  const smallC = slides[(index + 3) % slides.length];

  const tiles = [
    { src: slides[index], alt: "UK property", to: "/mortgages/first-time-buyer", label: "First-time buyer", keyPrefix: "main", classes: "col-span-3 row-span-2" },
    { src: smallA, alt: "English manor", to: "/mortgages/next-time-buyer", label: "Next-time buyer", keyPrefix: "a", classes: "col-span-3 row-span-1" },
    { src: smallB, alt: "English townhouse", to: "/mortgages/remortgage", label: "Remortgage", keyPrefix: "b", classes: "col-span-2 row-span-1" },
    { src: smallC, alt: "UK country cottage", to: "/mortgages/buy-to-let", label: "Buy-to-let", keyPrefix: "c", classes: "col-span-1 row-span-1" },
  ];

  return (
    <section data-testid="mosaic-section" className="relative bg-brand-ink py-28 sm:py-36 overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-12 lg:items-center">
          <Reveal className="lg:col-span-4">
            <p className="eyebrow text-brand-gold"><span className="rule-line" />The houses we finance</p>
            <h2 className="editorial-h mt-6 text-3xl text-white sm:text-4xl lg:text-5xl">
              From the <em className="text-brand-gold">first flat</em> to the family seat.
            </h2>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/65">
              Thatched cottages, Georgian terraces, London conversions and country homes — we
              arrange lending across the full UK property spectrum, with the same quiet attention
              to detail. Tap a tile to explore.
            </p>
            <div className="mt-8 flex items-center gap-3" aria-hidden>
              {slides.map((_, i) => (
                <span
                  key={i}
                  className={`h-px transition-all duration-500 ${i === index ? "w-10 bg-brand-gold" : "w-5 bg-white/20"}`}
                />
              ))}
            </div>
            <Link
              to="/mortgages/first-time-buyer"
              data-testid="mosaic-cta"
              className="group mt-8 inline-flex items-center gap-2 text-[0.72rem] font-semibold uppercase tracking-[0.32em] text-white transition-colors duration-200 hover:text-brand-gold"
            >
              Explore mortgages
              <ArrowRight size={14} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <div className="lg:col-span-8">
            <div className="grid grid-cols-6 grid-rows-2 gap-3 sm:gap-4" style={{ aspectRatio: "16 / 11" }}>
              {tiles.map((t) => (
                <Link
                  key={t.keyPrefix}
                  to={t.to}
                  data-testid={`mosaic-tile-${t.keyPrefix}`}
                  className={`group relative overflow-hidden ${t.classes}`}
                  aria-label={`${t.label} mortgages`}
                >
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={`${t.keyPrefix}-${index}`}
                      src={t.src}
                      alt={t.alt}
                      initial={{ opacity: 0, scale: 1.06 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                    />
                  </AnimatePresence>
                  <div className="absolute inset-0 bg-brand-ink/0 transition-colors duration-300 group-hover:bg-brand-ink/35" />
                  <div className="absolute inset-x-5 bottom-5 flex items-center justify-between text-white opacity-0 translate-y-2 transition-[opacity,transform] duration-300 group-hover:opacity-100 group-hover:translate-y-0">
                    <span className="text-[0.68rem] font-semibold uppercase tracking-[0.3em] drop-shadow">{t.label}</span>
                    <ArrowUpRight size={18} weight="bold" className="drop-shadow" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FeaturedQuotes = () => (
  <section data-testid="featured-quotes" className="bg-brand-bone py-24 sm:py-28 border-t border-black/5">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <p className="eyebrow text-brand"><span className="rule-line" />A few kind words</p>
      </Reveal>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {FEATURED_QUOTES.map((q, i) => (
          <Reveal key={q.name} delay={i * 0.1}>
            <figure
              data-testid={`featured-quote-${i}`}
              className="flex h-full flex-col justify-between border-l-2 border-brand-gold bg-white p-10"
            >
              <div>
                <Quotes size={28} weight="fill" className="text-brand-gold/60" />
                <blockquote className="editorial-h mt-6 text-xl leading-snug text-brand-ink">
                  {q.quote}
                </blockquote>
              </div>
              <figcaption className="mt-8">
                <p className="editorial-h text-lg text-brand-ink">{q.name}</p>
                <p className="mt-1 text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-neutral-500">{q.detail}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 text-[11px] leading-relaxed text-neutral-500 max-w-3xl">
        Reviews verified via Google. Each review reflects the individual client's experience — your outcome may differ.
      </p>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="bg-brand-bone py-28 sm:py-36">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <Eyebrow>How it works</Eyebrow>
        <h2 className="editorial-h mt-6 max-w-3xl text-3xl text-brand-ink sm:text-5xl lg:text-[3.4rem]">
          Three steps. <span className="italic text-brand">Zero jargon.</span>
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-6 md:grid-cols-3">
        {HOW_IT_WORKS.map((s, i) => {
          const Icon = HOW_ICONS[i];
          return (
            <Reveal key={s.title} delay={i * 0.1}>
              <div
                data-testid={`how-it-works-step-${i + 1}`}
                className="group h-full border border-black/10 bg-white p-10 transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-brand/40 hover:shadow-[0_28px_60px_rgba(10,19,38,0.1)]"
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-11 w-11 items-center justify-center border border-brand/30 text-brand">
                    <Icon size={20} weight="duotone" />
                  </span>
                  <span aria-hidden className="editorial-h text-5xl text-brand-ink/10">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="editorial-h mt-8 text-2xl text-brand-ink">{s.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-neutral-600">{s.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
      <Reveal delay={0.25}>
        <Link
          to="/contact"
          data-testid="how-it-works-cta"
          className="group mt-14 inline-flex items-center gap-2 bg-brand-ink px-8 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.22em] text-white transition-[background-color,transform] duration-200 hover:bg-brand hover:-translate-y-0.5"
        >
          Start with step one — no obligation
          <ArrowRight size={14} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const WhyBtk = () => (
  <section className="bg-white py-28 sm:py-36">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <Eyebrow>Why BTK</Eyebrow>
              <h2 className="editorial-h mt-6 text-3xl text-brand-ink sm:text-5xl lg:text-[3.2rem]">
                You're not a ticket<br />
                <span className="italic text-brand">in a queue.</span>
              </h2>
              <p className="mt-8 max-w-md text-base leading-relaxed text-neutral-600">
                A Berkshire-based practice looking after clients across Berkshire, London,
                Surrey and the rest of the UK — built on referrals, which means the only
                way we grow is by looking after you properly.
              </p>
            </Reveal>
          </div>
        </div>
        <div className="lg:col-span-7">
          <ol className="space-y-5">
            {WHY_BTK.map((w, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <Reveal key={w.title} delay={i * 0.08}>
                  <li className="flex gap-7 border-l-2 border-brand/30 bg-brand-bone/50 p-8 transition-[border-color,background-color] duration-300 hover:border-brand hover:bg-brand-bone">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-brand/30 text-brand">
                      <Icon size={20} weight="duotone" />
                    </span>
                    <div className="flex-1">
                      <h3 className="editorial-h text-xl text-brand-ink">{w.title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-neutral-600">{w.text}</p>
                    </div>
                    <span aria-hidden className="editorial-h text-4xl text-brand-ink/10">
                      0{i + 1}
                    </span>
                  </li>
                </Reveal>
              );
            })}
          </ol>
        </div>
      </div>
    </div>
  </section>
);

const ReviewsSection = () => (
  <section data-testid="reviews-section" className="bg-brand-bone py-28 sm:py-36">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="text-center">
        <Reveal>
          <Eyebrow className="justify-center inline-flex">Client reviews</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="editorial-h mt-6 mx-auto max-w-3xl text-3xl text-brand-ink sm:text-5xl lg:text-[3.2rem]">
            Berkshire, London, Surrey &amp; UK homeowners,<br />
            <span className="italic text-brand">in their own words.</span>
          </h2>
        </Reveal>
      </div>
      <Reveal delay={0.15}>
        <div className="mt-16">
          <TrustindexWidget />
        </div>
      </Reveal>
    </div>
  </section>
);

const CalculatorSection = () => (
  <section className="bg-white py-28 sm:py-36">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="mb-16">
        <Eyebrow>Repayment calculator</Eyebrow>
        <h2 className="editorial-h mt-6 max-w-2xl text-3xl text-brand-ink sm:text-5xl lg:text-[3.2rem]">
          Watch the monthly<br />
          <span className="italic text-brand">payment move.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <RepaymentCalculator />
      </Reveal>
      <Reveal delay={0.15}>
        <aside
          data-testid="calc-risk-warning"
          className="mt-10 border-l-2 border-brand bg-brand-bone p-6 text-[13px] leading-relaxed text-brand-ink sm:p-8"
        >
          <p className="text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-brand">
            Mortgage calculator risk warning
          </p>
          <p className="mt-3">{CALC_RISK_WARNING}</p>
        </aside>
      </Reveal>
    </div>
  </section>
);

const AboutTeaser = () => (
  <section className="relative bg-brand-ink py-28 sm:py-36 overflow-hidden">
    <img
      aria-hidden
      src={GALLERY[4]}
      alt=""
      className="absolute inset-0 h-full w-full object-cover opacity-20"
    />
    <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/90 to-brand-ink/70" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid items-center gap-16 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <div className="relative mx-auto max-w-xs">
            <div aria-hidden className="absolute -inset-3 border border-brand-gold/40" />
            <img
              src={BEN_PHOTO_URL}
              alt="Ben Kill CeMAP, Director of BTK Mortgage Services"
              className="relative aspect-[4/5] w-full object-cover grayscale-[0.1]"
            />
          </div>
        </Reveal>
        <div className="lg:col-span-8">
          <Reveal>
            <Eyebrow tone="gold">{ABOUT.heading}</Eyebrow>
            <blockquote className="editorial-h mt-8 max-w-3xl text-2xl text-white sm:text-3xl lg:text-4xl">
              <span className="font-serif text-brand-gold text-5xl leading-none">“</span>
              Personal service is at the forefront of my mission — relieving as much stress
              from your shoulders as possible.
            </blockquote>
            <p className="mt-8 text-[0.78rem] font-semibold uppercase tracking-[0.32em] text-white/60">
              {ABOUT.name} · {ABOUT.role}
            </p>
            <Link
              to="/about"
              data-testid="about-teaser-link"
              className="group mt-10 inline-flex items-center gap-2 text-[0.72rem] font-semibold uppercase tracking-[0.32em] text-white transition-colors duration-200 hover:text-brand-gold"
            >
              Read Ben's story
              <ArrowRight size={14} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const ReferBand = () => (
  <section className="bg-brand-bone py-24 sm:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <div className="flex flex-col items-start gap-8 border border-brand/20 bg-white p-10 sm:p-14 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <span className="inline-flex h-12 w-12 items-center justify-center border border-brand/30 text-brand">
              <Gift size={22} weight="duotone" />
            </span>
            <h2 className="editorial-h mt-8 text-3xl text-brand-ink sm:text-4xl lg:text-5xl">
              Know someone buying?<br />
              <span className="italic text-brand">Send them our way.</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-neutral-600">
              Most of our clients arrive by word of mouth. Refer a friend and when their
              mortgage completes, we'll send you a £100 John Lewis or Amazon voucher.
            </p>
          </div>
          <Link
            to="/resources/refer-a-friend"
            data-testid="refer-band-btn"
            className="group inline-flex shrink-0 items-center gap-2 bg-brand px-8 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.22em] text-white shadow-[0_14px_40px_rgba(1,86,207,0.35)] transition-[background-color,transform] duration-200 hover:bg-brand-dark hover:-translate-y-0.5"
          >
            Refer a friend
            <ArrowRight size={14} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const CtaBand = () => {
  const navigate = useNavigate();
  return (
    <section className="relative bg-brand-night py-28 sm:py-32 overflow-hidden">
      <img
        aria-hidden
        src={GALLERY[5]}
        alt=""
        className="absolute inset-0 h-full w-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-brand-night/95 via-brand-night/85 to-brand-night" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 text-center">
        <Reveal>
          <Eyebrow tone="gold" className="justify-center inline-flex">Enquire</Eyebrow>
          <h2 className="editorial-h mx-auto mt-8 max-w-3xl text-3xl text-white sm:text-5xl lg:text-[3.6rem]">
            Ready when you are.<br />
            <span className="italic text-brand-gold">The kettle's on.</span>
          </h2>
          <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-white/70">
            A no-pressure chat about your plans — by phone, email or WhatsApp.
            You'll leave knowing exactly where you stand.
          </p>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <button
              data-testid="cta-book-call-btn"
              onClick={() => navigate("/contact")}
              className="group inline-flex items-center gap-2 bg-brand px-8 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.22em] text-white transition-[background-color,transform] duration-200 hover:bg-brand-dark hover:-translate-y-0.5"
            >
              Arrange a consultation
              <ArrowRight size={14} weight="bold" />
            </button>
            <a
              href="tel:+441344578847"
              data-testid="cta-phone-link"
              className="inline-flex items-center border border-white/30 px-8 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.22em] text-white transition-[background-color,border-color] duration-200 hover:border-white hover:bg-white/5"
            >
              Call 01344 578847
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Home = () => (
  <main data-testid="home-page">
    <Seo path="/" />
    <Hero />
    <LenderMarquee />
    <Intro />
    <Services />
    <Mosaic />
    <HowItWorks />
    <WhyBtk />
    <FeaturedQuotes />
    <ReviewsSection />
    <CalculatorSection />
    <AboutTeaser />
    <ReferBand />
    <CtaBand />
  </main>
);

export default Home;
